import { watch } from 'vue';
import { useCustomizer } from '@fondationcss/ui';
import { useSdk } from '@fondationcss/sdk';
import { useRouter } from 'vue-router';
import { useSdk as useCustomSdk } from '@/composables/use-sdk';
import { FrequentationStatus } from '@/sdk/model';
import {
  menus,
  setStudentAssignmentsMenuVisible,
  setStudentStageMenuVisible,
} from '@/sidebar-items';

const STUDENT_PERMISSION = 'Parcours.LearningModule.Élève';

/**
 * Keeps the persisted menu-section index inside the range of the sections THIS user can see.
 *
 * `fondation-menu-section-idx` is stored per browser origin, but it indexes the
 * permission-FILTERED section list, which differs per user — and the framework neither clamps it
 * on read nor clears it when the signed-in user changes. So a staff session that ends on, say,
 * Gestion (visiting `/core/features` is enough) leaves `2` behind; the next student session has a
 * single section, `filteredMenuSections[2]` is `undefined`, every group resolves empty and the
 * sidebar renders « Vous n'avez accès à aucun module. » while the page itself loads perfectly.
 * It survives reload and re-login, because localStorage does.
 *
 * The framework's own repair (`useActiveMenuSync`) only fires when the current route matches a
 * menu entry's `to`, so it cannot help on a route that is in no menu — which is why this exists
 * even though pointing the Assignations entry at its canonical path fixed the reported case.
 *
 * Mirrors the framework's ACL for the `permissions` field only, and only for SECTIONS, which is
 * what the persisted index addresses. Item-level `feature` keys (Mes justificatifs, Mes rapports,
 * Démonstration) do not change the visible section count, so they do not affect this clamp.
 *
 * Known residual, unchanged by that: a section's `permissions` are the union of everything below
 * it, so they now OVER-approximate. A user whose only claim on a section is a permission whose
 * single item is feature-gated off — e.g. `Parcours.Demonstration.*` with the Demonstration flag
 * off (#712) — counts the section as visible here and renders it empty. Rare, since the sections
 * concerned carry other items, and it is the framework's item filtering rather than this clamp
 * that would have to become feature-aware. A section itself gaining a `module` or `feature` key
 * WOULD need this updated. The real fix belongs upstream, as a clamp inside the customizer.
 */
export function useMenuSectionGuard(): void {
  const sdk = useSdk();
  const customSdk = useCustomSdk();
  const customizer = useCustomizer();
  const router = useRouter();
  let stageMenuRequest = 0;

  watch(() => sdk.me.value?.permissions, (permissions) => {
    if (!permissions) return;
    const visible = menus.filter(s => !s.permissions || s.permissions.some(p => permissions.includes(p)));
    const highest = Math.max(visible.length - 1, 0);
    if (customizer.activeMenuSectionIdx.value > highest) {
      customizer.setActiveMenuSection(0, null);
    }
  }, { immediate: true });

  watch(
    [
      () => sdk.me.value?.user.id,
      () => sdk.me.value?.permissions.includes(STUDENT_PERMISSION) ?? false,
    ] as const,
    async ([userId, isStudent]) => {
      const request = ++stageMenuRequest;
      setStudentStageMenuVisible(false);
      setStudentAssignmentsMenuVisible(false);
      if (!userId || !isStudent) return;

      const [stagesResult, frequentationsResult] = await Promise.allSettled([
        customSdk.getMyAteWorkPlacements(),
        customSdk.myFrequentations(),
      ]);
      if (request !== stageMenuRequest) return;

      if (stagesResult.status === 'fulfilled') {
        const hasStage = stagesResult.value.length > 0;
        setStudentStageMenuVisible(hasStage);
        if (!hasStage && router.currentRoute.value.name === 'enseignement-mon-stage') {
          await router.replace({ name: 'enseignement-mon-tableau-de-bord' });
        }
      } else {
        console.error('Impossible de déterminer la disponibilité du menu Mon stage', stagesResult.reason);
      }

      if (frequentationsResult.status === 'fulfilled') {
        const hasIndividualAssignment = frequentationsResult.value.frequentations.some(
          frequentation => frequentation.status === FrequentationStatus.EnCours && frequentation.isIndividual,
        );
        setStudentAssignmentsMenuVisible(hasIndividualAssignment);
        if (
          !hasIndividualAssignment &&
          router.currentRoute.value.name === 'enseignement-assignation-individuelle'
        ) {
          await router.replace({ name: 'enseignement-mon-tableau-de-bord' });
        }
      } else {
        console.error(
          'Impossible de déterminer la disponibilité du menu Assignations',
          frequentationsResult.reason,
        );
      }
    },
    { immediate: true },
  );
}
